
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigResponse} from './ConfigResponse';
import {FieldModelConfigResponse} from './ConfigResponse';
import {CommissionProfile} from '../core/CommissionProfile';
import {FieldModelCommissionProfile} from '../core/CommissionProfile';

/**
 * CommissionProfileGetResponse struct
 */
class CommissionProfileGetResponse extends ConfigResponse {
  /**
   * Initialize struct
   * @param {!ConfigResponse=} parent
   * @param {!CommissionProfile=} commissionProfile
   * @constructor
   */
  constructor (parent = new ConfigResponse(), commissionProfile = new CommissionProfile()) {
    super()
    super.copy(parent)
    this.CommissionProfile = commissionProfile
  }

  /**
   * Copy struct (shallow copy)
   * @this {!CommissionProfileGetResponse}
   * @param {!CommissionProfileGetResponse} other Other struct
   * @returns {!CommissionProfileGetResponse} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.CommissionProfile != null) {
      this.CommissionProfile = CommissionProfile.fromObject(other.CommissionProfile)
    } else {
      this.CommissionProfile = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!CommissionProfileGetResponse}
   * @returns {!CommissionProfileGetResponse} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new CommissionProfileGetResponseModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new CommissionProfileGetResponseModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!CommissionProfileGetResponse}
   * @param {!CommissionProfileGetResponse} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof CommissionProfileGetResponse)) {
      throw new TypeError('Instance of CommissionProfileGetResponse is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!CommissionProfileGetResponse}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      CommissionProfile: ((this.CommissionProfile != null) ? this.CommissionProfile : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return CommissionProfileGetResponse.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!CommissionProfileGetResponse} other Object value
   * @returns {!CommissionProfileGetResponse} Created struct
   */
  static fromObject (other) {
    return new CommissionProfileGetResponse().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!CommissionProfileGetResponse}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return CommissionProfileGetResponse.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!CommissionProfileGetResponse}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6123
  }
}

export { CommissionProfileGetResponse };

/**
 * Fast Binary Encoding CommissionProfileGetResponse field model
 */
class FieldModelCommissionProfileGetResponse extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelConfigResponse(buffer, 4 + 4)
    this.CommissionProfile = new FieldModelCommissionProfile(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
  }

  /**
   * Get the ConfigResponse field model
   * @this {!FieldModelCommissionProfileGetResponse}
   * @returns {!FieldModelConfigResponse} ConfigResponse field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelCommissionProfileGetResponse}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelCommissionProfileGetResponse}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.CommissionProfile.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelCommissionProfileGetResponse}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.CommissionProfile.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelCommissionProfileGetResponse}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelCommissionProfileGetResponse.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelCommissionProfileGetResponse}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6123
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelCommissionProfileGetResponse}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelCommissionProfileGetResponse}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.CommissionProfile.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.CommissionProfile.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.CommissionProfile.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelCommissionProfileGetResponse}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelCommissionProfileGetResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelCommissionProfileGetResponse}
   * @param {!CommissionProfileGetResponse} fbeValue Default value, defaults is new CommissionProfileGetResponse()
   * @returns {!CommissionProfileGetResponse} CommissionProfileGetResponse value
   */
  get (fbeValue = new CommissionProfileGetResponse()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelCommissionProfileGetResponse}
   * @param {!CommissionProfileGetResponse} fbeValue CommissionProfileGetResponse value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.CommissionProfile.fbeSize) <= fbeStructSize) {
      fbeValue.CommissionProfile = this.CommissionProfile.get()
    } else {
      fbeValue.CommissionProfile = new CommissionProfile()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.CommissionProfile.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelCommissionProfileGetResponse}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelCommissionProfileGetResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelCommissionProfileGetResponse}
   * @param {!CommissionProfileGetResponse} fbeValue CommissionProfileGetResponse value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelCommissionProfileGetResponse}
   * @param {CommissionProfileGetResponse} fbeValue CommissionProfileGetResponse value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.CommissionProfile.set(fbeValue.CommissionProfile)
  }
}

export { FieldModelCommissionProfileGetResponse };

/**
 * Fast Binary Encoding CommissionProfileGetResponse model
 */
class CommissionProfileGetResponseModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelCommissionProfileGetResponse(this.buffer, 4)
  }

  /**
   * Get the CommissionProfileGetResponse model
   * @this {!CommissionProfileGetResponseModel}
   * @returns {!FieldModelCommissionProfileGetResponse} model CommissionProfileGetResponse model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!CommissionProfileGetResponseModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!CommissionProfileGetResponseModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return CommissionProfileGetResponseModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!CommissionProfileGetResponseModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelCommissionProfileGetResponse.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!CommissionProfileGetResponseModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!CommissionProfileGetResponseModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!CommissionProfileGetResponseModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!CommissionProfileGetResponseModel}
   * @param {!CommissionProfileGetResponse} value CommissionProfileGetResponse value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!CommissionProfileGetResponseModel}
   * @param {!CommissionProfileGetResponse} value CommissionProfileGetResponse value, defaults is new CommissionProfileGetResponse()
   * @return {!object} Deserialized CommissionProfileGetResponse value and its size
   */
  deserialize (value = new CommissionProfileGetResponse()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new CommissionProfileGetResponse(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new CommissionProfileGetResponse(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!CommissionProfileGetResponseModel}
   * @param {!number} prev Previous CommissionProfileGetResponse model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { CommissionProfileGetResponseModel };
